/**
 * Eine „Für Martin:“-Zeile in einzelne Neuerungen zerlegen. (17.10.2026)
 *
 * Martin, 17.10.2026, 08:44: „Was ist neu“ schöner formatieren, möglichst mit Screenshots, damit man besser weiß,
 * wonach man schauen soll. Bis dahin stand je Version EIN langer Absatz, mehrere Neuerungen per Semikolon verkettet.
 *
 * ## Das Format (abwärtskompatibel)
 *
 *   Für Martin: Neu-Chip: Rechts über dem Eingabefeld steht „Neu · n“. Wo: über dem Eingabefeld [bild:neu-chip.webp]; Soniox: …
 *
 *   - Einträge trennt ein Semikolon. Ohne Semikolon wird nach Sätzen zerlegt (alte Zeilen), ein Satz, der mit „Wo:“ oder
 *     einem Anschlusswort („Das“, „Dazu“, „Damit“ …) beginnt, bleibt beim vorigen. Beginnt eine Zeile ohne Semikolon
 *     mit „Titel: “, ist sie EIN Eintrag.
 *   - `Titel: Satz` — optional, Titel 1–5 Wörter vor dem ersten Doppelpunkt.
 *   - `Wo: …` — optional, der Ort in der App („Einstellungen > Erweitert“).
 *   - `[bild:name.webp]` — optional, ein Bild aus dem festen Ordner frontend/neu/ (ausgeliefert als /neu/<name>).
 *     Name nur [a-z0-9._-], Endung webp/png/jpg/jpeg, kein „..“. Alles andere wird verworfen — der Verweis
 *     verschwindet aus dem Text, ein Bild gibt es dann nicht.
 *
 * Rein und ohne DOM: der Server zerlegt (server/was-ist-neu.ts), die App zeigt nur (frontend/lib/neu-punkt.ts).
 */

/** Der Ordner der Bilder unter der Wurzel der App: frontend/neu → public/neu → /neu/<name>. */
export const NEU_BILD_ORDNER = "neu";

/** So viele Einträge trägt eine Zeile höchstens. */
export const PUNKTE_JE_ZEILE = 8;
export const TITEL_ZEICHEN = 48;
export const PUNKT_ZEICHEN = 280;
export const WO_ZEICHEN = 80;

/** Eine Neuerung, wie sie der Server zerlegt hat; `bild` ist ein geprüfter Dateiname oder leer. */
export interface NeuPunkt {
  titel: string;
  text: string;
  wo: string;
  bild: string;
}

const BILD_VERWEIS = /\[bild:([^\]]*)\]/giu;
const BILD_NAME = /^[a-z0-9][a-z0-9._-]{0,80}\.(?:webp|png|jpe?g)$/;

/** Ist das ein erlaubter Bildname (nur der Name, kein Pfad)? */
export function istBildName(name: string): boolean {
  return BILD_NAME.test(name) && !name.includes("..");
}

/** Ist das eine erlaubte Bildadresse, wie der Server sie liefert (`/neu/<name>`)? */
export function istBildUrl(url: string): boolean {
  const m = /^\/neu\/([^/]+)$/.exec(url);
  return !!m && istBildName(m[1] as string);
}

/** Die Zeile ohne Bildverweise — für alles, was Text liest (Vespera, Systemstand, das alte Feld `text`). */
export function ohneBildVerweise(t: string): string {
  return t.replace(BILD_VERWEIS, "").replace(/\s+([.;,!?])/g, "$1").replace(/\s{2,}/g, " ").trim();
}

/** Ein Satz, der nur weiterführt, gehört zum vorigen Eintrag. */
const ANSCHLUSS = /^(?:Wo:|Das|Dies|Diese[rsnm]?|Damit|Dazu|Dafür|Daher|Darum|Deshalb|So|Dann|Auch|Also|Er|Sie|Es|Dort|Hier)\b/u;
/** Satzende vor einem Großbuchstaben — nicht nach Abkürzungen wie „z. B.“ oder „Dr.“ (mindestens drei Zeichen davor). */
const SATZ_GRENZE = /(?<=(?:[\p{L}\p{N}]{3}|[)“”"»])[.!?])\s+(?=[\p{Lu}„"»])/u;
/** `Titel: Rest` — Titel ohne Satzzeichen, 1–5 Wörter. */
const TITEL = /^([^:;.!?„“"»«\n]{2,48}?):\s+(\S[\s\S]*)$/u;

function kuerze(t: string, max: number): string {
  if (t.length <= max) return t;
  const kopf = t.slice(0, max);
  return `${kopf.slice(0, Math.max(kopf.lastIndexOf(" "), 1)).replace(/[\s,;:–—-]+$/, "")} …`;
}

function grossAnfang(t: string): string {
  return t ? t.charAt(0).toLocaleUpperCase("de") + t.slice(1) : t;
}

/** `Titel: Rest` → [Titel, Rest]; sonst null. „Wo“ ist kein Titel. */
function titelVon(t: string): [string, string] | null {
  const m = TITEL.exec(t);
  if (!m) return null;
  const titel = (m[1] as string).trim();
  const woerter = titel.split(/\s+/).length;
  if (woerter > 5 || /^wo$/i.test(titel) || /^https?$/i.test(titel)) return null;
  return [titel, (m[2] as string).trim()];
}

/** Die Teile einer Zeile (noch roh, mit Bildverweisen). */
function teile(zeile: string): string[] {
  if (zeile.includes(";")) return zeile.split(";");
  if (titelVon(zeile)) return [zeile];
  const aus: string[] = [];
  for (const satz of zeile.split(SATZ_GRENZE)) {
    const s = satz.trim();
    if (!s) continue;
    const nurBild = ohneBildVerweise(s) === "";
    if (aus.length > 0 && (nurBild || ANSCHLUSS.test(s))) aus[aus.length - 1] += ` ${s}`;
    else aus.push(s);
  }
  return aus;
}

/**
 * Eine „Für Martin:“-Zeile (der Rest nach dem Doppelpunkt, schon ohne Code-Zeichen und Ticketnummern) in Einträge.
 * Rein; leere Zeile → keine Einträge.
 */
export function zerlegeFuerMartin(zeile: string): NeuPunkt[] {
  const aus: NeuPunkt[] = [];
  for (const roh of teile(zeile)) {
    let bild = "";
    const ohne = roh.replace(BILD_VERWEIS, (_g, name: string) => {
      const n = name.trim();
      if (!bild && istBildName(n)) bild = n;
      return " ";
    });
    let rest = ohne.replace(/\s{2,}/g, " ").trim();
    if (!rest) continue;
    let wo = "";
    const woAn = rest.search(/(?:^|[\s.!?])Wo:\s/u);
    if (woAn >= 0) {
      const ab = rest.indexOf("Wo:", woAn);
      wo = rest.slice(ab + 3).trim().replace(/[\s.;,]+$/, "");
      rest = rest.slice(0, ab).trim();
    }
    let titel = "";
    const t = titelVon(rest);
    if (t) [titel, rest] = t;
    rest = rest.replace(/[\s,;:–—-]+$/, "");
    if (!rest && titel) [titel, rest] = ["", titel];
    if (!rest) continue;
    let text = grossAnfang(kuerze(rest, PUNKT_ZEICHEN));
    if (!/[.!?…“”"]$/u.test(text)) text += ".";
    aus.push({
      titel: kuerze(titel, TITEL_ZEICHEN).replace(/[.:]+$/, ""),
      text,
      wo: kuerze(wo, WO_ZEICHEN),
      bild,
    });
    if (aus.length >= PUNKTE_JE_ZEILE) break;
  }
  return aus;
}
